import {
    afterNextRender,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    inject,
    input,
    InputSignal,
    output,
    OutputEmitterRef,
    signal,
    Signal,
    WritableSignal
} from "@angular/core";
import {ResponsiveDirective} from "../../responsive/responsive.directive";
import {ResponsiveService} from "../../responsive/responsive.service";
import {OrderingContextIntention} from '../../ordering-context/ordering-context';
import {OrderingContextModel} from '../../ordering-context/ordering-context.model';
import {ButtonComponent} from "../../buttons/button/button.component";
import {ButtonProminence, ButtonTier} from "../../buttons/button/button";
import {LinkComponent} from "../../buttons/link/link.component";
import {StoreData} from "../store-search.model";
import {OrderType} from '../../order/order';
import {NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {ToggleComponent} from "../../ui-elements/toggle/toggle.component";
import {StoreStatusPanelComponent} from "../store-status-panel/store-status-panel.component";
import {StoreMapPanelComponent} from "../store-map-panel/store-map-panel.component";
import {ImageCacheDirective} from '../../ui-elements/image/image-cache.directive';
import {DistancePipe} from '../distance.pipe';
import {CssVarDirective} from '../../utils/css-var.directive';
import {TouchableDirective} from '../../ui-elements/touchable.directive';
import {ConfigurationModel} from '../../configuration/configuration.model';
import {ActivatableDirective} from '../../ui-elements/activatable.directive';

@Component({
    selector: "woa-store-details-panel",
    templateUrl: "./store-details-panel.component.html",
    styleUrl: "./store-details-panel.component.scss",
    imports: [
        ButtonComponent,
        CssVarDirective,
        DistancePipe,
        ImageCacheDirective,
        LinkComponent,
        NgClass,
        ResponsiveDirective,
        StoreMapPanelComponent,
        StoreStatusPanelComponent,
        ToggleComponent,
        TouchableDirective,
        TranslateModule,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StoreDetailsPanelComponent {
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public responsiveService: ResponsiveService = inject(ResponsiveService);
    private orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    private changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

    static readonly TEXT_LINE_HEIGHT: number = 20;

    StoreDetailsPanelComponent = StoreDetailsPanelComponent;
    ButtonTier = ButtonTier;
    ButtonProminence = ButtonProminence;
    OrderType = OrderType;

    public readonly store: InputSignal<StoreData> = input.required<StoreData>();
    public readonly intention: InputSignal<OrderingContextIntention> = input.required<OrderingContextIntention>();

    public readonly filteredOrderType: InputSignal<OrderType> = input<OrderType>();
    public readonly summary: InputSignal<boolean> = input<boolean>(false);
    public readonly showStateWithName: InputSignal<boolean> = input<boolean>(false);
    public readonly showDistance: InputSignal<boolean> = input<boolean>(true);
    public readonly showStoreImage: InputSignal<boolean> = input<boolean>(false);
    public readonly showAddress: InputSignal<boolean> = input<boolean>(true);
    public readonly showDescription: InputSignal<boolean> = input<boolean>(true);
    public readonly collapseText: InputSignal<boolean> = input<boolean>(false);
    public readonly showTelephone: InputSignal<boolean> = input<boolean>(false);
    public readonly showMoreDetails: InputSignal<boolean> = input<boolean>(true);
    public readonly showStatus: InputSignal<boolean> = input<boolean>(true);
    public readonly showActions: InputSignal<boolean> = input<boolean>(true);
    public readonly showMap: InputSignal<boolean> = input<boolean>(false);

    public onStoreDetails: OutputEmitterRef<void> = output<void>();
    public onOrderFromStore: OutputEmitterRef<OrderType> = output<OrderType>();

    public readonly textCollapsed: WritableSignal<boolean> = signal(true);

    public readonly canShowDistance: Signal<boolean> = computed(() => this.showDistance() && this.store() && this.store().distance != undefined);
    public readonly canShowDescription: Signal<boolean> = computed(() => this.showDescription() && !!this.store().description);
    public readonly canShowTelephone: Signal<boolean> = computed(() => this.showTelephone() && this.store() && !!this.store().telephone);
    public readonly canShowActions: Signal<boolean> = computed(() => this.showActions() && this.intention() !== OrderingContextIntention.VIEW_CURRENT_STORE);
    public readonly canShowOrderTypes: Signal<boolean> = computed(() => !this.filteredOrderType() && !this.orderingContextModel.singleOrderType());
    public readonly canShowMap: Signal<boolean> = computed(() => this.showMap() && this.store() && ((!!this.store().latitude && !!this.store().longitude) || (!!this.store().locationImage.id && !!this.store().locationImage.image)));

    public readonly canOrder: Signal<(orderType: OrderType) => boolean> = computed(() => {
        const store: StoreData = this.store();
        const filteredOrderType: OrderType = this.filteredOrderType();
        return (orderType: OrderType): boolean => {
            return store && store.canOrder(orderType) && (filteredOrderType == undefined || filteredOrderType === orderType);
        };
    });

    constructor() {
        // Force change detection of text scroll height has been set
        afterNextRender(() => this.changeDetectorRef.markForCheck());
    }

    orderHere(orderType?: OrderType): void {
        this.onOrderFromStore.emit(orderType ? orderType
            : this.canOrder()(OrderType.ORDER_AHEAD) ? OrderType.ORDER_AHEAD
                : this.canOrder()(OrderType.ORDER_DELIVERY) ? OrderType.ORDER_DELIVERY
                    : OrderType.ORDER_AT_TABLE);
    }

    canCollapseText(text: HTMLElement): boolean {
        return this.collapseText() && text.scrollHeight > StoreDetailsPanelComponent.TEXT_LINE_HEIGHT + 2;
    }

    toggleText(text: HTMLElement): void {
        if (this.canCollapseText(text)) {
            this.textCollapsed.set(!this.textCollapsed());
            text.style.maxHeight = (this.textCollapsed() ? StoreDetailsPanelComponent.TEXT_LINE_HEIGHT : text.scrollHeight) + "px";
        }
    }

    getDirections(): void {
        // noinspection SpellCheckingInspection
        window.open("https://maps.apple.com/?daddr=" + this.store().latitude + "," + this.store().longitude, "_system", "noopener");
    }

    callPhone(): void {
        window.open("tel:" + this.store().telephone, "_system");
    }
}
